import {
  type ChangeEvent,
  type MouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useRef,
  useState,
} from 'react';
import { generatePath } from 'react-router-dom';

import * as Select from '@radix-ui/react-select';

import {
  BaseSelect,
  type IBaseSelectProps,
} from '../../../../components/BaseSelect';
import { TEMPLATE_VIEW } from '../../../../constants/RoutePaths';
import { type ITemplatePageMinimizedDTO } from '../../../../services/templates';
import { filterByTitle } from '../../../../utils/filters';
import { getRouteFrom } from '../../../../utils/getRoutes';
import {
  ActionButtonContainer,
  ArrowDown,
  ArrowDownContainer,
  ArrowUp,
  ArrowUpContainer,
  Container,
  Fieldset,
  IndicatorContainer,
  Input,
  Item,
  ItemContainer,
  NoTemplateContainer,
  NoTemplateText,
  PreviewIcon,
  PreviewLink,
  SelectIcon,
  Viewport,
} from './styles';

export interface ISelectTemplatesProps
  extends Omit<IBaseSelectProps, 'children'> {
  data: ITemplatePageMinimizedDTO[];
  dataCy?: string;
  loading?: boolean;
  onPreviewData?: (dataItem: ITemplatePageMinimizedDTO) => void;
  onValueChange?: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
}
export const SelectTemplates = ({
  data,
  dataCy = 'select-template-field',
  loading = false,
  onPreviewData,
  onValueChange,
  onOpenChange,
  ...props
}: ISelectTemplatesProps): JSX.Element => {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePreviewData = (
    e: MouseEvent<HTMLAnchorElement>,
    dataItem: ITemplatePageMinimizedDTO
  ) => {
    e.stopPropagation();
    onPreviewData?.(dataItem);
  };

  const handleSearchCompany = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const newSearch = target.value.trim();
    setSearch(newSearch);
  };

  const handlePressOutOfInput = ({
    key,
  }: ReactKeyboardEvent<HTMLDivElement>) => {
    if (/^([A-Za-z0-9_ ])$/.test(key)) {
      inputRef.current?.focus();
    }
  };

  const handleSelectOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    open || setSearch('');
  };

  const dataFiltered = filterByTitle(data, search);

  return (
    <Container data-cy="container-select-templates">
      <BaseSelect
        {...props}
        dataCy={dataCy}
        onKeyPress={handlePressOutOfInput}
        onOpenChange={handleSelectOpenChange}
        onValueChange={onValueChange}
        disabled={loading}
        placeholder="Select a template"
        customContentStyle={{
          overflow: 'visible !important',
          height: '100%',
          maxHeight: '40vh',
          width: '100%',
          position: 'absolute',
          left: '12px',
        }}
      >
        <Fieldset data-cy="templates-fieldSet">
          <Input
            data-cy="input-searchTemplates"
            defaultValue={search}
            disabled={loading}
            onChange={handleSearchCompany}
            placeholder="Search template"
            ref={inputRef}
            type="text"
          />
        </Fieldset>
        <ArrowUpContainer data-cy="arrow-up">
          <ArrowUp />
        </ArrowUpContainer>

        <Viewport data-cy="itens-container">
          {dataFiltered.length > 0 ? (
            dataFiltered.map((data) => {
              const { id, title } = data;
              return (
                <ItemContainer key={id} data-cy={title}>
                  <Item
                    value={`${id}`}
                    data-cy="item-content"
                    disabled={loading}
                  >
                    <Select.ItemText data-cy="item-text">
                      {title}
                    </Select.ItemText>
                    <IndicatorContainer data-cy="item-selected-indicator">
                      <SelectIcon />
                    </IndicatorContainer>
                  </Item>
                  <ActionButtonContainer data-cy="container-actionButton">
                    <PreviewLink
                      data-cy="preview-link"
                      target="_blank"
                      to={generatePath(getRouteFrom(TEMPLATE_VIEW), {
                        templateId: id,
                      })}
                      onClick={(e) => {
                        handlePreviewData(e, data);
                      }}
                      title="Preview"
                    >
                      <PreviewIcon />
                    </PreviewLink>
                  </ActionButtonContainer>
                </ItemContainer>
              );
            })
          ) : (
            <NoTemplateContainer>
              <NoTemplateText data-cy="text-noTemplate-found">
                No templates found
              </NoTemplateText>
            </NoTemplateContainer>
          )}
        </Viewport>
        <ArrowDownContainer data-cy="arrow-down">
          <ArrowDown />
        </ArrowDownContainer>
      </BaseSelect>
    </Container>
  );
};
